import { memo, useState, type ReactNode } from "react";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { AddBusiness, Handshake } from "@mui/icons-material";
import { AddAffiliationDialog } from "../affiliation/components/AddAffiliationDialog";
import { PendingProviderRequestCard } from "../affiliation/components/PendingProviderRequestCard";
import { ReceivedAffiliationRequests } from "../affiliation/components/ReceivedAffiliationRequests";
import { SelectProviderDialog } from "../affiliation/components/SelectProviderDialog";
import { useCanSetupTeleconsultoria } from "../affiliation/hooks/useCanManageTeleconsultoriaAffiliations";
import { useTeleconsultoriaSetup } from "../affiliation/hooks/useTeleconsultoriaSetup";

const NO_BLOCKED_TOKENS = new Set<string>();

function ChoiceCard({
  icon,
  title,
  text,
  actionLabel,
  onAction,
}: {
  icon: ReactNode;
  title: string;
  text: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
      <Stack spacing={1.5} alignItems="flex-start" height="100%">
        <Box sx={{ color: "primary.main", display: "flex" }} aria-hidden>
          {icon}
        </Box>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {text}
        </Typography>
        <Button
          variant="contained"
          onClick={onAction}
          sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" } }}
        >
          {actionLabel}
        </Button>
      </Stack>
    </Paper>
  );
}

/**
 * Tela inicial da Teleconsultoria (extensão ativa, ainda sem relação):
 * a clínica escolhe se vai PRESTAR (adiciona a 1ª afiliada) ou RECEBER
 * (pede afiliação a uma prestadora). Só gestor/dono escolhe.
 * Com pedido enviado: só o "Aguardando aprovação" (não presta e pede ao mesmo tempo).
 * Pedidos recebidos de outras clínicas aparecem abaixo para aprovar/recusar.
 */
export const TeleconsultoriaOnboarding = memo(function TeleconsultoriaOnboarding() {
  const canSetup = useCanSetupTeleconsultoria();
  const {
    becomeProvider,
    requestProvider,
    pendingRequest,
    isLoadingPending,
    cancelRequest,
    checkApproval,
    isSaving,
  } = useTeleconsultoriaSetup();
  const [openDialog, setOpenDialog] = useState<"provide" | "receive" | null>(null);
  const closeDialog = () => setOpenDialog(null);

  if (!canSetup) {
    return (
      <Alert severity="info">
        Sua clínica ainda não presta nem recebe teleconsultoria. Peça ao gestor para
        definir se a clínica presta ou recebe teleconsultoria.
      </Alert>
    );
  }

  // Primeira leitura do pedido: espera para não piscar as escolhas antes do "Aguardando".
  if (isLoadingPending && !pendingRequest) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={28} aria-label="Carregando" />
      </Stack>
    );
  }

  if (pendingRequest) {
    return (
      <Stack spacing={3} width="100%">
        <PendingProviderRequestCard
          request={pendingRequest}
          isBusy={isSaving}
          onCheck={checkApproval}
          onCancel={() => void cancelRequest()}
        />
        <ReceivedAffiliationRequests />
      </Stack>
    );
  }

  return (
    <Stack spacing={2} width="100%">
      <ReceivedAffiliationRequests />
      <Stack spacing={0.5}>
        <Typography variant="subtitle1" fontWeight={700}>
          Como sua clínica vai usar a teleconsultoria?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Escolha uma opção. Uma clínica presta OU recebe teleconsultoria.
        </Typography>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <ChoiceCard
          icon={<AddBusiness fontSize="large" />}
          title="Prestar teleconsultoria"
          text="Seus profissionais atendem outras clínicas. Adicione a primeira clínica que vai receber a teleconsultoria da sua organização."
          actionLabel="Adicionar afiliada"
          onAction={() => setOpenDialog("provide")}
        />
        <ChoiceCard
          icon={<Handshake fontSize="large" />}
          title="Receber teleconsultoria"
          text="Seus profissionais marcam com os teleconsultores de outra clínica. Escolha a prestadora; o pedido fica aguardando a aprovação dela."
          actionLabel="Selecionar prestadora"
          onAction={() => setOpenDialog("receive")}
        />
      </Box>
      <AddAffiliationDialog
        isOpen={openDialog === "provide"}
        isSaving={isSaving}
        blockedTokens={NO_BLOCKED_TOKENS}
        onClose={closeDialog}
        onConfirm={becomeProvider}
      />
      <SelectProviderDialog
        isOpen={openDialog === "receive"}
        isSaving={isSaving}
        onClose={closeDialog}
        onConfirm={requestProvider}
      />
    </Stack>
  );
});
